/**
 * SearchPanel Widget
 *
 * Search panel component for finding services
 * Features:
 * - Search input with icon
 * - Search button
 * - Clean, rounded design
 * - Optional autocomplete dropdown with options
 */

'use client';

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Input } from './Input';
import { PrimaryButton } from '@/src/shared/ui/button/PrimaryButton';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Icon } from '@/src/shared/ui/icons/Icon';
import { cn } from '@/src/shared/lib/utils';

export interface SearchOption {
  id: string;
  label: string;
  sublabel?: string;
  value: string;
}

export interface SearchPanelProps {
  /**
   * Search value
   */
  value: string;

  /**
   * On value change handler
   */
  onChange: (value: string) => void;

  /**
   * On search submit handler
   */
  onSearch: () => void;

  /**
   * Placeholder text
   * @default 'Search for any service...'
   */
  placeholder?: string;

  /**
   * Additional class names
   */
  className?: string;

  /**
   * Button text
   * @default 'Search'
   */
  buttonText?: string;

  /**
   * Options for autocomplete dropdown
   */
  options?: SearchOption[];

  /**
   * Callback when an option is selected from dropdown
   */
  onOptionSelect?: (option: SearchOption) => void;

  /**
   * Show dropdown even when input is empty
   * @default false
   */
  showAllOptions?: boolean;

  /**
   * Loading state for options
   * @default false
   */
  optionsLoading?: boolean;

  /**
   * Currently selected option id
   */
  selectedOptionId?: string;
}

export function SearchPanel({
  value,
  onChange,
  onSearch,
  placeholder = 'Search for any service...',
  className,
  buttonText = 'Search',
  options,
  onOptionSelect,
  showAllOptions = false,
  optionsLoading = false,
  selectedOptionId,
}: SearchPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search value
  const filteredOptions = useMemo(() => {
    if (!options) return [];
    if (!value.trim() && !showAllOptions) return options;

    const query = value.toLowerCase().trim();
    if (!query) return options;

    return options.filter((option) =>
      option.label.toLowerCase().includes(query) ||
      option.sublabel?.toLowerCase().includes(query)
    );
  }, [options, value, showAllOptions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredOptions.length]);

  const handleInputFocus = () => {
    if (options && options.length > 0) {
      setIsOpen(true);
    }
  };

  const handleInputChange = (newValue: string) => {
    onChange(newValue);
    if (options && options.length > 0) {
      setIsOpen(true);
    }
  };

  const handleOptionSelect = (option: SearchOption) => {
    onChange(option.label);
    setIsOpen(false);
    setHighlightedIndex(-1);
    onOptionSelect?.(option);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!options || options.length === 0) {
      if (e.key === 'Enter') {
        onSearch();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleOptionSelect(filteredOptions[highlightedIndex]);
        } else if (!isOpen && options.length > 0) {
          setIsOpen(true);
        } else {
          onSearch();
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
    }
  };

  const handleSearchClick = () => {
    // If dropdown is open and there's a highlighted option, select it
    if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
      handleOptionSelect(filteredOptions[highlightedIndex]);
      return;
    }
    // If there's exactly one filtered option, select it automatically
    if (filteredOptions.length === 1 && onOptionSelect) {
      handleOptionSelect(filteredOptions[0]);
      return;
    }
    // Close dropdown and trigger search
    setIsOpen(false);
    onSearch();
  };

  const handleClear = () => {
    onChange('');
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const hasOptions = options && options.length > 0;
  const showDropdown = isOpen && hasOptions && (filteredOptions.length > 0 || optionsLoading);

  return (
    <div ref={containerRef} className={cn('relative flex sm:flex-row gap-3 w-full', className)}>
      <div className="flex h-[60px] flex-row w-full shadow-panel rounded-full">
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            value={value}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            leftIcon="icon"
            leftIconColor="secondary"
            className="flex-1 rounded-r-none h-full"
          />

          {/* Clear button */}
          {value && hasOptions && (
            <button
              onClick={handleClear}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:opacity-70 transition-opacity z-10"
              type="button"
            >
              <Icon name="x" size="sm" color="secondary" />
            </button>
          )}
        </div>

        <PrimaryButton
          onClick={handleSearchClick}
          className="h-full px-4 sm:px-8 lg:px-10 whitespace-nowrap rounded-l-none w-[110px] sm:w-[170px] lg:w-[220px] shrink-0"
        >
          <Typography variant="h3" color="inherit" weight="semibold">
            {buttonText}
          </Typography>
        </PrimaryButton>
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div
          className={cn(
            'absolute top-full left-0 right-0 mt-2',
            'bg-white',
            'rounded-[24px]',
            'shadow-lg',
            'max-h-[400px]',
            'overflow-y-auto',
            'py-2',
            'z-40',
            'search-panel-dropdown-scrollbar'
          )}
        >
          {optionsLoading ? (
            <div className="px-6 py-4 text-center">
              <Typography variant="body" color="secondary">
                Loading...
              </Typography>
            </div>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const isSelected = selectedOptionId === option.id;
              const isHighlighted = index === highlightedIndex;

              return (
                <div
                  key={option.id}
                  className={cn(
                    'px-6 py-3 mx-2',
                    'cursor-pointer',
                    'transition-all duration-200',
                    'rounded-[12px]',
                    isSelected && 'bg-accent-tertiary',
                    isHighlighted && !isSelected && 'bg-surface-hover',
                    !isSelected && !isHighlighted && 'hover:bg-surface-hover'
                  )}
                  onClick={() => handleOptionSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <Typography variant="body" color="primary" weight="medium">
                    {option.label}
                  </Typography>
                  {option.sublabel && (
                    <Typography variant="bodySm" color="secondary">
                      {option.sublabel}
                    </Typography>
                  )}
                </div>
              );
            })
          ) : (
            <div className="px-6 py-4 text-center">
              <Typography variant="body" color="tertiary">
                No results found for "{value}"
              </Typography>
            </div>
          )}
        </div>
      )}

      <style jsx global>{`
        .search-panel-dropdown-scrollbar::-webkit-scrollbar {
          width: 8px;
        }

        .search-panel-dropdown-scrollbar::-webkit-scrollbar-track {
          background: transparent;
          margin: 8px 0;
        }

        .search-panel-dropdown-scrollbar::-webkit-scrollbar-thumb {
          background: #6ab344;
          border-radius: 4px;
        }

        .search-panel-dropdown-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #5a9e38;
        }

        .search-panel-dropdown-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #6ab344 transparent;
        }
      `}</style>
    </div>
  );
}
